/**
 * Accesso con CIE, CNS e SPID OpenID Connect tramite i Relying Party su GovAuth
 * (progetto cie-oidc-utd). Il RP riporta il cittadino su questa pagina con
 * ?cie_code=... (da riscattare con v1/login_cie) oppure ?cie_error=...
 *
 * Quali pulsanti mostrare lo decide il backoffice, per comune: flag login_spid_old,
 * login_password, login_spid_new, login_cie, login_cns su rcweb.cliente, restituiti dalla homepage
 * in municipalityData.login_metodi. Lo SPID attuale (Shibboleth, SAML) è il
 * pulsante SPIDReactButton.
 *
 * Grafica, testi e loghi (assets/img/login) seguono la Discovery Page ufficiale
 * IT-Wallet: github.com/italia/eid-wallet-it-docs, official_resources/discovery-page.
 * Due sezioni: identità digitale (CIE, SPID) e metodi alternativi (CNS, password).
 */

import logoCie from '../assets/img/login/cie.svg';
import logoSpid from '../assets/img/login/spid.svg';
import logoCns from '../assets/img/login/cns.svg';

const HOST_LOGIN = 'https://loginrp.megtributi.it';
const HOST_CNS = 'https://logincns.megtributi.it';

export const URL_IDENTITA_DIGITALE = 'https://identitadigitale.gov.it/';

// Ordine di presentazione come nella Discovery Page: CIE, SPID, poi i metodi alternativi
const METODI = {
    cie: {
        sezione: 'digitale',
        etichetta: 'Entra con CIE',
        logo: logoCie,
        descrizione: "La CIE (Carta d'Identità Elettronica) è il documento d'identità elettronico italiano. Usala per accedere ai servizi online in modo sicuro.",
        link: 'https://www.cartaidentita.interno.gov.it/',
        linkEtichetta: 'Scopri come ottenerla'
    },
    spid_new: {
        sezione: 'digitale',
        etichetta: 'Entra con SPID',
        logo: logoSpid,
        descrizione: "SPID (Sistema Pubblico di Identità Digitale) è il sistema pubblico di identità digitale italiano. Scegli il tuo gestore di identità per accedere con le tue credenziali SPID.",
        link: 'https://www.spid.gov.it/',
        linkEtichetta: 'Scopri come ottenerla'
    },
    cns: {
        sezione: 'alternativo',
        etichetta: 'Entra con CNS',
        logo: logoCns,
        descrizione: "La CNS (Carta Nazionale dei Servizi) è lo strumento di identificazione che consente ai cittadini di accedere ai servizi della pubblica amministrazione online. Serve un lettore di smart card collegato al computer."
    }
};

// Testi del "Scopri di più" per lo SPID attuale (SPIDReactButton)
export const SPID = METODI.spid_new;

export const MESSAGGI_ERRORE = {
    sessione: "L'accesso non è stato completato o è scaduto. Riprova.",
    attributi: "Il gestore dell'identità non ha fornito il codice fiscale. Riprova o usa un altro metodo di accesso.",
    cns: "Nessuna carta rilevata. Inserisci la tessera nel lettore, chiudi e riapri il browser (oppure apri una finestra anonima) e riprova.",
    provider: "Gestore dell'identità non riconosciuto. Riprova."
};

function esc(testo) {
    return String(testo).replace(/[&<>"']/g, (c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
}

function safariSuMac() {
    const ua = navigator.userAgent;
    return /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|Firefox/.test(ua);
}

/**
 * Popup con le istruzioni da leggere PRIMA di aprire il RP CNS: il browser chiede il
 * certificato appena si collega a logincns, quindi non si possono mostrare dopo.
 * Con `fallito` (ritorno da cie_error=cns) il popup spiega come ripartire da zero,
 * perché il browser ricorda la scelta del certificato e una pagina web non può azzerarla.
 */
export function mostraIstruzioniCns({fallito = false} = {}) {
    const comune = window.municipalityData || {};
    const contatti = [comune.email ? `<a href="mailto:${esc(comune.email)}">${esc(comune.email)}</a>` : '',
        comune.telefono ? `<a href="tel:${esc(comune.telefono)}">${esc(comune.telefono)}</a>` : ''].filter(Boolean).join(' · ');
    const html = `<div style="text-align:left">
        ${safariSuMac() ? '<p><strong>Safari non è supportato per la CNS</strong>: apri questa pagina con Chrome, Edge o Firefox.</p>' : ''}
        ${fallito ? '<p>Nessuna carta rilevata o certificato non accettato. Per riprovare chiudi del tutto il browser e riaprilo (oppure apri una finestra anonima), poi ripeti questi passi.</p>' : ''}
        <ol>
            <li>Collega il lettore e inserisci la carta <strong>ora</strong>.</li>
            <li>Usa Chrome, Edge o Firefox (su Mac non Safari).</li>
            <li>Nella finestra del browser scegli il certificato di <strong>autenticazione</strong> (non quello di firma).</li>
            <li>Inserisci il PIN della carta.</li>
        </ol>
        <p><small>Se compare «accesso negato» (ERR_BAD_SSL_CLIENT_AUTH_CERT): aggiorna il programma della carta e riprova.<br/>
        Se non compare la scelta del certificato: chiudi del tutto il browser e riaprilo.</small></p>
        ${contatti ? `<p><small>Serve aiuto? ${contatti}</small></p>` : ''}
    </div>`;
    return window.SweetAlert.fire({
        title: fallito ? 'Accesso con CNS non riuscito' : 'Prima di continuare',
        html,
        icon: fallito ? 'warning' : 'info',
        showCancelButton: true,
        cancelButtonText: 'Annulla',
        confirmButtonText: fallito ? 'Riprova' : 'Ho inserito la carta, continua'
    }).then((r) => {
        if (r.isConfirmed) window.location.assign(url('cns'));
    });
}

function flags() {
    return (window.municipalityData && window.municipalityData.login_metodi) || {};
}

function url(metodo) {
    // alpignano-to: lo stesso codice usato nel percorso del RP
    const codice = window.getMunicipalityStringUrl();
    if (metodo === 'cns') return `${HOST_CNS}/${codice}/utd/cns`;
    if (metodo === 'spid_new') return `${HOST_LOGIN}/${codice}/utd/login?metodo=spid`;
    return `${HOST_LOGIN}/${codice}/utd/login`;
}

/** Il pulsante SPID attuale: visibile salvo flag login_spid_old spento o SPID nuovo attivo. */
export function spidOldAttivo() {
    const f = flags();
    return f.spid_old !== false && !f.spid_new;
}

/** Form codice fiscale/email + password: visibile salvo flag login_password spento. */
export function passwordAttivo() {
    return flags().password !== false;
}

/** Pulsanti nuovi attivi per il comune, nella sezione indicata ('digitale' o 'alternativo'). */
export function metodiAttivi(sezione) {
    const f = flags();
    return Object.keys(METODI)
        .filter((metodo) => f[metodo] && (!sezione || METODI[metodo].sezione === sezione))
        .map((metodo) => ({metodo, ...METODI[metodo], url: url(metodo)}));
}
